import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase/firestore.js';
import { getLiveSpacesPage, getLiveSpacesByCity, getLiveSpacesCityCount } from '../firebase/publicSpaces.js';

// Public marketing-site read of real listings, filtered to status=='live'
// only — delegates to the same functions the main app's public space/host
// pages already use (src/firebase/database.js), rather than a second,
// parallel query implementation.
export async function fetchLiveSpaces({ city, pageSize = 12 } = {}) {
  const { spaces } = city
    ? await getLiveSpacesByCity({ city, pageSize })
    : await getLiveSpacesPage({ pageSize });
  return spaces;
}

// "Showing X of Y" line's Y — the given city's own live count when one was
// searched (getLiveSpacesCityCount), otherwise the platform-wide count. An
// aggregate query either way (one read regardless of match count), not a
// second full fetch.
export async function fetchLiveSpacesCount(city) {
  if (city) return getLiveSpacesCityCount(city);
  const snap = await getCountFromServer(query(collection(db, 'spaces'), where('status', '==', 'live')));
  return snap.data().count;
}

// Mirrors formatDimensions + spaceType from src/features/spaces/Constant.js —
// reimplemented locally rather than imported, for the same bundle-isolation
// reason as fetchLiveSpaces above.
export function formatSpaceMeta(space) {
  const width = space?.dimensions?.width;
  const height = space?.dimensions?.height;
  const rawUnit = space?.dimensions?.unit;
  const unit = rawUnit === 'Feet (ft)' ? 'ft' : rawUnit === 'Metres (m)' ? 'm' : rawUnit;
  const dims = width && height ? `${width}×${height}${unit || ''}` : '';
  return [space?.spaceType, dims].filter(Boolean).join(' · ');
}

// Mirrors formatSpacePrice from Constant.js, same reason.
export function formatSpacePriceLabel(space) {
  const number = space?.price?.number ?? space?.pricePerPeriod;
  const unitRaw = space?.price?.unit ?? space?.pricePeriod;
  if (!number) return '';
  const short = unitRaw === 'Week' ? 'wk' : unitRaw === 'Month' ? 'mo' : 'day';
  return `₹${Number(number).toLocaleString('en-IN')} / ${short}`;
}

// The hero search's budget buckets ("Under ₹50,000/mo" etc.) are monthly —
// normalizes any listing's real price (which may be quoted per day, week, or
// month) onto the same monthly-equivalent scale so matchesHeroBudget's
// thresholds mean the same thing regardless of how the host priced it.
export function monthlyEquivalentPrice(space) {
  const number = Number(space?.price?.number ?? space?.pricePerPeriod) || 0;
  const unit = space?.price?.unit ?? space?.pricePeriod;
  if (unit === 'Day') return number * 30;
  if (unit === 'Week') return number * 4.345;
  return number; // Month, or already unitless/monthly
}
